import {
  Apple,
  Cake,
  CakeSlice,
  Candy,
  Cherry,
  Coffee,
  Cookie,
  CookingPot,
  Croissant,
  CupSoda,
  Dessert,
  Egg,
  EggFried,
  GlassWater,
  IceCreamBowl,
  IceCreamCone,
  Leaf,
  Milk,
  Moon,
  Nut,
  Popcorn,
  Popsicle,
  Salad,
  Sandwich,
  Soup,
  Utensils,
  UtensilsCrossed,
  Wheat,
  type LucideIcon,
} from "lucide-react";

/**
 * Resolves /src/assets/recipes/... paths stored in DB to actual build-time URLs
 * using Vite's import.meta.glob for eager loading of all recipe images.
 */
const recipeImages = import.meta.glob<{ default: string }>(
  '/src/assets/recipes/*.webp',
  { eager: true }
);

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

/**
 * Given an image path (potentially a local asset path like /src/assets/recipes/xxx.webp),
 * returns the resolved URL usable in <img src>.
 * Handles: full URLs, local asset paths, and Supabase storage relative paths.
 */
/**
 * Builds a Supabase Storage URL, optionally using the transform API for thumbnails.
 * thumbnail=true  → /render/image/public/...?width=400&quality=60  (~40-80KB)
 * thumbnail=false → /object/public/...                              (original)
 */
const buildStorageUrl = (path: string, _thumbnail: boolean): string => {
  const bucket = path.startsWith('product-images/') ? '' : 'product-images/';
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}${path}`;
};

export const resolveRecipeImage = (
  imagePath: string | null | undefined,
  thumbnail = false,
): string | null => {
  if (!imagePath) return null;

  // Already a full Supabase storage URL — optionally switch to render endpoint
  if (imagePath.startsWith('http')) {
      return imagePath;
  }

  // Local asset path — resolve via glob map (can't transform)
  const mod = recipeImages[imagePath];
  if (mod) return mod.default;

  // Storage relative path
  if (SUPABASE_URL && !imagePath.startsWith('/src/')) {
    return buildStorageUrl(imagePath, thumbnail);
  }

  return imagePath;
};

/* ═══════════════════════════════════════════
   IMAGE GROUP → STORAGE FALLBACK
   Maps image_group keys to storage paths in the product-images bucket.
   Upload a single representative image per group and the entire
   group inherits it automatically.
   ═══════════════════════════════════════════ */

const IMAGE_GROUP_STORAGE: Record<string, string> = {
  churros:          "fallbacks/churros.webp",
  cakes:            "fallbacks/cakes.webp",
  cookies:          "fallbacks/cookies.webp",
  puddings:         "fallbacks/puddings.webp",
  jams:             "fallbacks/jams.webp",
  cinnamon_rolls:   "fallbacks/cinnamon_rolls.webp",
  breads:           "fallbacks/breads.webp",
  toppings:         "fallbacks/toppings.webp",
  cold_desserts:    "fallbacks/cold_desserts.webp",
  sweet_snacks:     "fallbacks/sweet_snacks.webp",
  brownies:         "fallbacks/brownies.webp",
  cheesecakes:      "fallbacks/cheesecakes.webp",
  gelatin:          "fallbacks/gelatin.webp",
  mousse:           "fallbacks/mousse.webp",
  truffles:         "fallbacks/truffles.webp",
  flan:             "fallbacks/flan.webp",
  panna_cotta:      "fallbacks/panna_cotta.webp",
  crumble:          "fallbacks/crumble.webp",
  ice_cream:        "fallbacks/ice_cream.webp",
  popsicles:        "fallbacks/popsicles.webp",
  energy_bars:      "fallbacks/energy_bars.webp",
  energy_balls:     "fallbacks/energy_balls.webp",
  soups:            "fallbacks/soups.webp",
  tacos:            "fallbacks/tacos.webp",
  salads:           "fallbacks/salads.webp",
  pasta:            "fallbacks/pasta.webp",
  eggs:             "fallbacks/eggs.webp",
  smoothies:        "fallbacks/smoothies.webp",
  salsas:           "fallbacks/salsas.webp",
  drinks:           "fallbacks/drinks.webp",
};

/**
 * Resolves an image_group key to a full public storage URL.
 * Returns null if the group is unknown or not mapped.
 */
export const resolveImageGroup = (
  imageGroup: string | null | undefined,
  thumbnail = false,
): string | null => {
  if (!imageGroup || !SUPABASE_URL) return null;
  const key = imageGroup.toLowerCase().trim().replace(/\s+/g, "_");
  const path = IMAGE_GROUP_STORAGE[key];
  if (!path) return null;
  return buildStorageUrl(`product-images/${path}`, thumbnail);
};

/**
 * Full recipe image resolution chain:
 * 1. recipe.image (own image — URL, local asset, or storage path)
 * 2. recipe.image_group (group fallback from storage)
 * 3. Category visual fallback (icone de traco, sem imagem real)
 *
 * Returns { url, type } where type indicates the source.
 */
export const resolveRecipeImageFull = (
  image: string | null | undefined,
  imageGroup: string | null | undefined,
  category: string | null | undefined,
  thumbnail = false,
): { url: string | null; type: "own" | "group" | "category_fallback" } => {
  // 1. Own image
  const own = resolveRecipeImage(image, thumbnail);
  if (own) return { url: own, type: "own" };

  // 2. Image group
  const group = resolveImageGroup(imageGroup, thumbnail);
  if (group) return { url: group, type: "group" };

  // 3. Fallback de categoria (sem URL — quem chama usa getCategoryFallback / RecipeFallbackCover)
  return { url: null, type: "category_fallback" };
};

/* ═══════════════════════════════════════════
   CATEGORY FALLBACK SYSTEM
   Capa desenhada para receita sem foto.
   Antes era emoji + gradiente de cor crua (from-rose-200…): duas coisas que
   o app não usa mais. Agora a categoria só escolhe um ícone de traço; o
   fundo é hsl(var(--muted)) e mora em <RecipeFallbackCover>.
   ═══════════════════════════════════════════ */

type CategoryFallback = {
  icon: LucideIcon;
};

const CATEGORY_FALLBACKS: Record<string, CategoryFallback> = {
  // Postres & Repostería
  postres:       { icon: CakeSlice },
  reposteria:    { icon: Croissant },
  churros:       { icon: Croissant },
  panes:         { icon: Wheat },
  galletas:      { icon: Cookie },
  pasteles:      { icon: Cake },
  coberturas:    { icon: Dessert },
  "coberturas_y_rellenos": { icon: Dessert },
  "frostings_&_fillings": { icon: Dessert },
  helados:       { icon: IceCreamCone },
  brownie:       { icon: CakeSlice },
  brownies:      { icon: CakeSlice },
  cheesecake:    { icon: CakeSlice },
  gelatina:      { icon: Dessert },
  mousse:        { icon: IceCreamBowl },
  trufa:         { icon: Candy },
  trufas:        { icon: Candy },
  flan:          { icon: Dessert },
  pudin:         { icon: Dessert },
  pudines:       { icon: Dessert },
  panna_cotta:   { icon: Dessert },
  crumble:       { icon: IceCreamBowl },
  panque:        { icon: Cake },
  vasito:        { icon: IceCreamBowl },
  vasitos:       { icon: IceCreamBowl },
  mermeladas:    { icon: Cherry },
  mermelada:     { icon: Cherry },
  bolitas:       { icon: Nut },
  barras:        { icon: Wheat },
  paletas:       { icon: Popsicle },
  bombones:      { icon: Candy },
  nieve:         { icon: IceCreamCone },
  // Sopas & Caldos
  sopas:         { icon: Soup },
  caldos:        { icon: Soup },
  cremas:        { icon: Soup },
  // Principales
  platos_fuertes:{ icon: UtensilsCrossed },
  guisados:      { icon: CookingPot },
  // O lucide não tem taco, tortilla nem wrap. Sandwich é o desenho mais
  // próximo de comida de mão; o resto cai no talher genérico em vez de
  // virar metáfora torta.
  tacos:         { icon: Sandwich },
  ensaladas:     { icon: Salad },
  pastas:        { icon: Wheat },
  picadillo:     { icon: CookingPot },
  quesadillas:   { icon: Sandwich },
  enchiladas:    { icon: Sandwich },
  sopes:         { icon: Utensils },
  tlacoyos:      { icon: Utensils },
  gorditas:      { icon: Utensils },
  molotes:       { icon: Utensils },
  tostadas:      { icon: Utensils },
  tinga:         { icon: CookingPot },
  chilaquiles:   { icon: Utensils },
  // Desayunos
  desayuno:      { icon: EggFried },
  desayunos:     { icon: EggFried },
  huevos:        { icon: Egg },
  omelette:      { icon: Egg },
  frittata:      { icon: Egg },
  wrap:          { icon: Sandwich },
  // Bebidas
  bebidas:       { icon: CupSoda },
  smoothies:     { icon: Milk },
  smoothie:      { icon: Milk },
  jugos:         { icon: Apple },
  aguas:         { icon: GlassWater },
  te:            { icon: Coffee },
  licuado:       { icon: Milk },
  licuados:      { icon: Milk },
  horchata:      { icon: Milk },
  chocolate_caliente: { icon: Coffee },
  cafe:          { icon: Coffee },
  limonada:      { icon: GlassWater },
  // Snacks
  snacks:        { icon: Nut },
  botanas:       { icon: Popcorn },
  chips:         { icon: Popcorn },
  palomitas:     { icon: Popcorn },
  rollitos:      { icon: Sandwich },
  // Salsas
  salsas:        { icon: Soup },
  salsa:         { icon: Soup },
  guacamole:     { icon: Leaf },
  // Almuerzo / Cena
  almuerzo:      { icon: Salad },
  cena:          { icon: Moon },
  cenas_ligeras: { icon: Moon },
  // Gelatinas
  gelatinas:     { icon: Dessert },
  // Panes & Muffins
  "breads_&_muffins": { icon: Wheat },
  muffins:       { icon: Cake },
  // Tortillas
  tortilla:      { icon: Utensils },
  // EN equivalents
  breakfast:     { icon: EggFried },
  lunch:         { icon: Salad },
  dinner:        { icon: Moon },
  desserts:      { icon: CakeSlice },
  soups:         { icon: Soup },
  salads:        { icon: Salad },
  drinks:        { icon: CupSoda },
  cookies:       { icon: Cookie },
  ice_cream:     { icon: IceCreamCone },
  jams:          { icon: Cherry },
  bread:         { icon: Wheat },
  pasta:         { icon: Wheat },
};

const DEFAULT_FALLBACK: CategoryFallback = {
  icon: Utensils,
};

/**
 * Ícone de traço da categoria, para a capa de receita sem foto.
 * Normaliza o nome da categoria (minúscula, sem acento, espaço vira "_").
 */
export const getCategoryFallback = (category: string | null | undefined): CategoryFallback => {
  if (!category) return DEFAULT_FALLBACK;
  const key = category
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");
  return CATEGORY_FALLBACKS[key] || DEFAULT_FALLBACK;
};

/** List of all available image_group keys for UI dropdowns */
export const IMAGE_GROUP_KEYS = Object.keys(IMAGE_GROUP_STORAGE).sort();

/**
 * Canonical check: does a recipe have a real photo (own or group)?
 * Returns false for category_fallback (só a capa desenhada, sem foto).
 * Use this everywhere instead of local hasRealImage copies.
 */
export const hasRealImage = (recipe: {
  image?: string | null;
  image_group?: string | null;
  category?: string | null;
}): boolean => {
  const { type } = resolveRecipeImageFull(recipe.image, recipe.image_group, recipe.category);
  return type === "own" || type === "group";
};
